import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { pluriel } from '@/lib/pluriel';
import { AdminButton } from "@/components/admin/button";
import { Card, CardBody } from "@/components/admin/card";
import { ChipFilter } from "@/components/admin/chip-filter";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import { Pagination } from "@/components/admin/pagination";
import { Tag } from "@/components/admin/tag";
import { useInvoices } from "@/lib/finance/finance-hooks";
import { fmtFCFA } from "@/lib/format";
import type { TagClass } from "@/lib/status";
import { inputStyle } from "./_ui";

const PAGE = 25;

const STATUS_TABS = [
	{ key: "", label: "Toutes" },
	{ key: "ISSUED", label: "Émises" },
	{ key: "PARTIAL", label: "Partielles" },
	{ key: "PAID", label: "Payées" },
	{ key: "OVERDUE", label: "En retard" },
	{ key: "CANCELLED", label: "Annulées" },
];

const STATUS_CLASS: Record<string, TagClass> = {
	ISSUED: "info",
	PARTIAL: "warning",
	PAID: "success",
	OVERDUE: "danger",
	CANCELLED: "neutral",
};

const TYPE_CLASS: Record<string, TagClass> = {
	B2C: "neutral",
	B2B: "terra",
	PROFORMA: "ochre",
};

/** FIN-02 — Liste des factures. */
export function AdminInvoicesPage() {
	const navigate = useNavigate();
	const [sp] = useSearchParams();
	const [status, setStatus] = useState(sp.get("status") ?? "");
	const [type, setType] = useState("");
	const [q, setQ] = useState("");
	const [page, setPage] = useState(1);

	const filter = useMemo(
		() => ({
			status: status || undefined,
			type: type || undefined,
			q: q.trim() || undefined,
			limit: PAGE,
			offset: (page - 1) * PAGE,
		}),
		[status, type, q, page],
	);
	const { data, isLoading, isError } = useInvoices(filter);
	const items = data?.items ?? [];
	const total = data?.total ?? 0;
	const totalPages = Math.max(1, Math.ceil(total / PAGE));

	return (
		<>
			<PageHead
				title="Factures"
				sub={`${pluriel(total, 'facture')} · numérotation séquentielle DGI`}
				actions={
					<AdminButton
						variant="primary"
						onClick={() => navigate("/finance/factures/nouvelle")}
					>
						<AdminIcon n="plus" s={14} />
						Émettre une facture
					</AdminButton>
				}
			/>

			<Card>
				<CardBody>
					<div
						style={{
							display: "flex",
							flexWrap: "wrap",
							gap: 8,
							marginBottom: 12,
							alignItems: "center",
						}}
					>
						{STATUS_TABS.map((t) => (
							<ChipFilter
								key={t.key}
								on={status === t.key}
								onClick={() => {
									setStatus(t.key);
									setPage(1);
								}}
							>
								{t.label}
							</ChipFilter>
						))}
						<span
							style={{
								width: 1,
								height: 20,
								background: "var(--line, #e3e3e3)",
								margin: "0 4px",
							}}
						/>
						{["", "B2C", "B2B", "PROFORMA"].map((tp) => (
							<ChipFilter
								key={tp || "all"}
								on={type === tp}
								onClick={() => {
									setType(tp);
									setPage(1);
								}}
							>
								{tp || "Tous types"}
							</ChipFilter>
						))}
						<input
							placeholder="Recherche n° / client…"
							value={q}
							onChange={(e) => {
								setQ(e.target.value);
								setPage(1);
							}}
							style={{
								...inputStyle,
								marginLeft: "auto",
								minWidth: 200,
								flex: "1 1 200px",
								width: "auto",
							}}
						/>
					</div>

					<div className="table-wrap" style={{ margin: 0 }}>
						<table className="table">
							<thead>
								<tr>
									<th>N° facture</th>
									<th>Type</th>
									<th>Client</th>
									<th>Émise le</th>
									<th>Échéance</th>
									<th className="right">Total TTC</th>
									<th className="right">Restant dû</th>
									<th>Statut</th>
								</tr>
							</thead>
							<tbody>
								{isLoading && (
									<tr>
										<td
											colSpan={8}
											className="muted"
											style={{ textAlign: "center", padding: 24 }}
										>
											Chargement…
										</td>
									</tr>
								)}
								{isError && (
									<tr>
										<td
											colSpan={8}
											className="muted"
											style={{ textAlign: "center", padding: 24 }}
										>
											Erreur de chargement.
										</td>
									</tr>
								)}
								{!isLoading && items.length === 0 && (
									<tr>
										<td
											colSpan={8}
											className="muted"
											style={{ textAlign: "center", padding: 24 }}
										>
											Aucune facture.
										</td>
									</tr>
								)}
								{items.map((inv) => (
									<tr
										key={inv.id}
										style={{ cursor: "pointer" }}
										onClick={() => navigate(`/finance/factures/${inv.id}`)}
									>
										<td className="mono">
											<strong>{inv.invoiceNo}</strong>
										</td>
										<td>
											<Tag variant={TYPE_CLASS[inv.type] ?? "neutral"} noDot>
												{inv.type}
											</Tag>
										</td>
										<td>{inv.customerSnapshot?.name}</td>
										<td className="muted" style={{ fontSize: 12 }}>
											{inv.issuedAt}
										</td>
										<td className="muted" style={{ fontSize: 12 }}>
											{inv.dueAt ?? "—"}
										</td>
										<td className="right tn">
											<strong>{fmtFCFA(inv.totalTtc)}</strong>
										</td>
										<td className="right tn">
											{fmtFCFA(inv.totalTtc - inv.amountPaid)}
										</td>
										<td>
											<Tag variant={STATUS_CLASS[inv.status] ?? "neutral"}>
												{inv.status}
											</Tag>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					{total > PAGE && (
						<Pagination
							from={(page - 1) * PAGE + 1}
							to={Math.min(page * PAGE, total)}
							total={total}
							current={page}
							totalPages={totalPages}
							onChange={setPage}
						/>
					)}
				</CardBody>
			</Card>
		</>
	);
}
